import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { jobsApi } from '@/lib/api/jobs.api';
import type {
  Job,
  JobFormData,
  JobFilters,
  JobsListResponse,
  PaginationConfig,
  ScrapingQueueItem,
  QueueFilters,
  ScrapingQueueResponse,
  DuplicateGroup,
  DuplicateFilters,
  DuplicatesResponse,
  JobStats,
} from '@repo/types';
import { toast } from 'sonner';

// ==================== Query Keys ====================

export const jobKeys = {
  all: ['jobs'] as const,
  lists: () => [...jobKeys.all, 'list'] as const,
  list: (filters?: JobFilters, pagination?: Partial<PaginationConfig>) =>
    [...jobKeys.lists(), filters, pagination] as const,
  details: () => [...jobKeys.all, 'detail'] as const,
  detail: (id: string) => [...jobKeys.details(), id] as const,
  skills: (query: string) => [...jobKeys.all, 'skills', query] as const,
  categories: () => [...jobKeys.all, 'categories'] as const,
  locations: (query: string) => [...jobKeys.all, 'locations', query] as const,
  industries: () => [...jobKeys.all, 'industries'] as const,
  stats: () => [...jobKeys.all, 'stats'] as const,
};

export const scrapingKeys = {
  all: ['scraping'] as const,
  lists: () => [...scrapingKeys.all, 'list'] as const,
  list: (filters?: QueueFilters, pagination?: Partial<PaginationConfig>) =>
    [...scrapingKeys.lists(), filters, pagination] as const,
  details: () => [...scrapingKeys.all, 'detail'] as const,
  detail: (id: string) => [...scrapingKeys.details(), id] as const,
};

export const duplicateKeys = {
  all: ['duplicates'] as const,
  lists: () => [...duplicateKeys.all, 'list'] as const,
  list: (filters?: DuplicateFilters, pagination?: Partial<PaginationConfig>) =>
    [...duplicateKeys.lists(), filters, pagination] as const,
  details: () => [...duplicateKeys.all, 'detail'] as const,
  detail: (groupId: string) => [...duplicateKeys.details(), groupId] as const,
};

// ==================== Jobs Queries ====================

/**
 * Fetch list of jobs with filters and pagination
 */
export function useJobs(
  filters?: JobFilters,
  pagination?: Partial<PaginationConfig>,
  options?: Omit<UseQueryOptions<JobsListResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: jobKeys.list(filters, pagination),
    queryFn: () => jobsApi.getJobs(filters, pagination),
    ...options,
  });
}

/**
 * Fetch single job by ID
 */
export function useJob(id: string, options?: Omit<UseQueryOptions<Job>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: jobKeys.detail(id),
    queryFn: () => jobsApi.getJob(id),
    enabled: !!id,
    ...options,
  });
}

/**
 * Fetch skills autocomplete
 */
export function useSkills(query: string, options?: Omit<UseQueryOptions<string[]>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: jobKeys.skills(query),
    queryFn: () => jobsApi.getSkills(query),
    enabled: query.length >= 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

/**
 * Fetch categories list
 */
export function useCategories(options?: Omit<UseQueryOptions<string[]>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: jobKeys.categories(),
    queryFn: () => jobsApi.getCategories(),
    staleTime: 10 * 60 * 1000, // 10 minutes
    ...options,
  });
}

/**
 * Fetch locations autocomplete
 */
export function useLocations(query: string, options?: Omit<UseQueryOptions<string[]>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: jobKeys.locations(query),
    queryFn: () => jobsApi.getLocations(query),
    enabled: query.length >= 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

/**
 * Fetch industries list
 */
export function useIndustries(options?: Omit<UseQueryOptions<string[]>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: jobKeys.industries(),
    queryFn: () => jobsApi.getIndustries(),
    staleTime: 10 * 60 * 1000, // 10 minutes
    ...options,
  });
}

/**
 * Fetch job statistics
 */
export function useJobStats(options?: Omit<UseQueryOptions<JobStats>, 'queryKey' | 'queryFn'>) {
  return useQuery({
    queryKey: jobKeys.stats(),
    queryFn: () => jobsApi.getJobStats(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

// ==================== Jobs Mutations ====================

/**
 * Create new job
 */
export function useCreateJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: JobFormData) => jobsApi.createJob(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      // Invalidate categories since new jobs may add new categories
      queryClient.invalidateQueries({ queryKey: jobKeys.categories() });
      if (result.duplicateWarning?.isDuplicate) {
        toast.warning(`Job created, but ${result.duplicateWarning.matches?.length || 0} potential duplicates found`);
      } else {
        toast.success('Job created successfully');
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create job');
    },
  });
}

/**
 * Update existing job
 */
export function useUpdateJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<JobFormData> }) => jobsApi.updateJob(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      queryClient.invalidateQueries({ queryKey: jobKeys.detail(variables.id) });
      // Invalidate categories if category was updated
      if (variables.data.category !== undefined) {
        queryClient.invalidateQueries({ queryKey: jobKeys.categories() });
      }
      toast.success('Job updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update job');
    },
  });
}

/**
 * Delete job
 */
export function useDeleteJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => jobsApi.deleteJob(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      toast.success('Job deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete job');
    },
  });
}

/**
 * Bulk update job status
 */
export function useBulkUpdateJobs() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ jobIds, status }: { jobIds: string[]; status: 'active' | 'draft' | 'expired' }) =>
      jobsApi.bulkUpdateStatus(jobIds, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      toast.success('Jobs updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update jobs');
    },
  });
}


/**
 * Export jobs to CSV
 */
export function useExportJobs() {
  return useMutation({
    mutationFn: (filters?: JobFilters) => jobsApi.exportJobs(filters),
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `jobs-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Jobs exported successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to export jobs');
    },
  });
}

/**
 * Check for duplicate jobs
 */
export function useCheckDuplicates() {
  return useMutation({
    mutationFn: (data: Partial<JobFormData>) => jobsApi.checkDuplicates(data),
  });
}

// ==================== Scraping Queue ====================

/**
 * Fetch scraping queue
 */
export function useScrapingQueue(
  filters?: QueueFilters,
  pagination?: Partial<PaginationConfig>,
  options?: Omit<UseQueryOptions<ScrapingQueueResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: scrapingKeys.list(filters, pagination),
    queryFn: () => jobsApi.getScrapingQueue(filters, pagination),
    refetchInterval: (query) => {
      // Auto-refetch if there are processing items
      const data = query.state.data;
      const hasProcessing = data?.queueItems?.some(
        (item: ScrapingQueueItem) => item.status === 'processing' || item.status === 'retrying'
      );
      return hasProcessing ? 5000 : false; // 5 seconds
    },
    ...options,
  });
}

/**
 * Fetch single scraping queue item
 */
export function useScrapingQueueItem(
  id: string,
  options?: Omit<UseQueryOptions<ScrapingQueueItem>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: scrapingKeys.detail(id),
    queryFn: () => jobsApi.getScrapingQueueItem(id),
    enabled: !!id,
    refetchInterval: (query) => {
      // Auto-refetch while processing
      const data = query.state.data;
      return data?.status === 'processing' || data?.status === 'retrying' ? 5000 : false;
    },
    ...options,
  });
}

/**
 * Queue job for scraping
 */
export function useScrapeJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ url, formInputData, priority }: { url: string; formInputData?: Partial<JobFormData>; priority?: number }) =>
      jobsApi.scrapeJob(url, formInputData, priority),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scrapingKeys.lists() });
      toast.success('Job queued for scraping');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to queue job for scraping');
    },
  });
}

/**
 * Retry failed scraping job
 */
export function useRetryScrapingJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => jobsApi.retryScrapingJob(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scrapingKeys.lists() });
      toast.success('Scraping job queued for retry');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to retry scraping job');
    },
  });
}

/**
 * Cancel pending scraping job
 */
export function useCancelScrapingJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => jobsApi.cancelScrapingJob(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scrapingKeys.lists() });
      toast.success('Scraping job cancelled');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to cancel scraping job');
    },
  });
}

/**
 * Delete scraping queue item
 */
export function useDeleteQueueItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => jobsApi.deleteQueueItem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scrapingKeys.lists() });
      toast.success('Queue item deleted');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete queue item');
    },
  });
}

/**
 * Update queue item priority
 */
export function useUpdateQueuePriority() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, priority }: { id: string; priority: number }) => jobsApi.updateQueuePriority(id, priority),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scrapingKeys.lists() });
      toast.success('Priority updated');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update priority');
    },
  });
}

// ==================== Duplicates ====================

/**
 * Fetch duplicate groups
 */
export function useDuplicates(
  filters?: DuplicateFilters,
  pagination?: Partial<PaginationConfig>,
  options?: Omit<UseQueryOptions<DuplicatesResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: duplicateKeys.list(filters, pagination),
    queryFn: () => jobsApi.getDuplicates(filters, pagination),
    ...options,
  });
}

/**
 * Fetch duplicate group comparison
 */
export function useDuplicateComparison(
  groupId: string,
  options?: Omit<UseQueryOptions<DuplicateGroup>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: duplicateKeys.detail(groupId),
    queryFn: () => jobsApi.getDuplicateComparison(groupId),
    enabled: !!groupId,
    ...options,
  });
}

/**
 * Resolve duplicate group
 */
export function useResolveDuplicates() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groupId,
      action,
      keptJobId,
      comments,
    }: {
      groupId: string;
      action: string;
      keptJobId?: string;
      comments?: string;
    }) => jobsApi.resolveDuplicates(groupId, action, keptJobId, comments),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: duplicateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      toast.success('Duplicates resolved');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to resolve duplicates');
    },
  });
}

/**
 * Merge duplicate jobs
 */
export function useMergeDuplicates() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ jobIds, mergedData }: { jobIds: string[]; mergedData: JobFormData }) =>
      jobsApi.mergeDuplicates(jobIds, mergedData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: duplicateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      toast.success('Jobs merged successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to merge jobs');
    },
  });
}

/**
 * Mark as not duplicate
 */
export function useMarkNotDuplicate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, comments }: { groupId: string; comments?: string }) =>
      jobsApi.markNotDuplicate(groupId, comments),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: duplicateKeys.lists() });
      toast.success('Marked as not duplicate');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to mark as not duplicate');
    },
  });
}

// ==================== Bulk Upload ====================

/**
 * Parse CSV for bulk upload
 */
export function useParseBulkUpload() {
  return useMutation({
    mutationFn: (file: File) => jobsApi.parseBulkUpload(file),
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to parse file');
    },
  });
}

/**
 * Import jobs from bulk upload
 */
export function useImportBulkUpload() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (rows: JobFormData[]) => jobsApi.importBulkUpload(rows),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      toast.success(`${result.imported} jobs imported successfully${result.failed > 0 ? `, ${result.failed} failed` : ''}`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to import jobs');
    },
  });
}

/**
 * Download bulk upload template
 */
export function useDownloadTemplate() {
  return useMutation({
    mutationFn: () => jobsApi.downloadTemplate(),
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'job-upload-template.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Template downloaded');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to download template');
    },
  });
}
